







































































































































































import { API } from "@/shared/api";
import { Component, Vue, Prop } from "vue-property-decorator";
import { AIFormBuilderService } from "../services/AIFormBuilderService";
import { FormBuilderConfig } from "../types/FormBuilderTypes";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

@Component
export default class FormBuilderAIChat extends Vue {
  @Prop({ default: null }) readonly existingForm!: FormBuilderConfig | null;

  private aiService = new AIFormBuilderService();

  public messages: ChatMessage[] = [];
  public userInput: string = "";
  public loading: boolean = false;
  public fetchingApi: boolean = false;
  public apiEndpointBeingFetched: string = "";
  public showPreviewDialog: boolean = false;
  public generatedConfig: FormBuilderConfig | null = null;

  public async mounted() {
    // Si hay un formulario existente, proporcionar contexto a la IA
    if (this.existingForm) {
      console.log("📝 Modo edición - Formulario existente:", this.existingForm);

      this.messages.push({
        role: "assistant",
        content: `¡Hola! Veo que quieres editar el formulario **"${this.existingForm.formName}"**. 

Actualmente tiene:
- **${this.existingForm.fields.length}** campos
- **${this.existingForm.tabs.length}** tabs

¿Qué te gustaría cambiar? Puedo ayudarte a:
- Agregar nuevos campos
- Eliminar campos existentes
- Modificar propiedades de campos
- Reorganizar el formulario
- Cambiar la configuración de APIs`,
      });

      // Proporcionar contexto completo a la IA
      await this.aiService.provideFormContext(this.existingForm);
    }
  }

  public async sendMessage() {
    if (!this.userInput.trim()) return;

    const userMessage = this.userInput.trim();
    this.userInput = "";

    // Add user message to chat
    this.messages.push({
      role: "user",
      content: userMessage,
    });

    this.scrollToBottom();
    this.loading = true;

    try {
      const response = await this.aiService.sendMessage(userMessage);

      // Check if AI wants to fetch an API
      const action = this.aiService.extractAction(response);

      if (action && action.action === "fetch_api") {
        // Don't show the AI response yet, handleApiFetch will handle communication
        await this.handleApiFetch(action.endpoint);
      } else if (action && action.action === "generate_form") {
        // Show AI response first, then handle form generation
        this.messages.push({
          role: "assistant",
          content: response,
        });
        this.scrollToBottom();
        this.handleFormGeneration(action.config);
      } else {
        // Normal AI response
        this.messages.push({
          role: "assistant",
          content: response,
        });
        this.scrollToBottom();
      }
    } catch (error) {
      this.messages.push({
        role: "assistant",
        content: `❌ Error: ${(error as Error).message}`,
      });
      this.scrollToBottom();
    } finally {
      this.loading = false;
    }
  }

  private async handleApiFetch(endpoint: string) {
    this.fetchingApi = true;
    this.apiEndpointBeingFetched = endpoint;

    // Show AI message about fetching
    this.messages.push({
      role: "assistant",
      content: `🔍 Consultando la API **${endpoint}**...`,
    });
    this.scrollToBottom();

    try {
      const { ssmHttpService } = await import("@/shared/services/http-service");

      // Normalize endpoint
      let normalizedEndpoint = endpoint.startsWith("/")
        ? endpoint
        : "/" + endpoint;

      if (!endpoint.startsWith("http")) {
        let url = endpoint.startsWith("/")
          ? endpoint.replace("/", "")
          : endpoint;
        normalizedEndpoint = API.webApiBase + url;
      }

      const data = await ssmHttpService.get(normalizedEndpoint);

      // Show success message with data preview
      const dataPreview =
        Array.isArray(data) && data.length > 0
          ? `Encontré **${
              data.length
            } registros**. Campos disponibles: \`${Object.keys(data[0]).join(
              "`, `"
            )}\``
          : "Datos obtenidos correctamente";

      this.messages.push({
        role: "assistant",
        content: `✅ ${dataPreview}`,
      });
      this.scrollToBottom();

      // Provide context to AI and get continuation
      const aiResponse = await this.aiService.provideApiContext(endpoint, data);

      this.messages.push({
        role: "assistant",
        content: aiResponse,
      });
      this.scrollToBottom();

      // Check if AI generated form after context
      const action = this.aiService.extractAction(aiResponse);
      if (action && action.action === "generate_form") {
        this.handleFormGeneration(action.config);
      }
    } catch (error) {
      this.messages.push({
        role: "assistant",
        content: `❌ No pude consultar la API **${endpoint}**. Error: ${
          (error as Error).message
        }. Por favor verifica que el endpoint sea correcto.`,
      });
      this.scrollToBottom();
    } finally {
      this.fetchingApi = false;
      this.apiEndpointBeingFetched = "";
    }
  }

  private handleFormGeneration(config: FormBuilderConfig) {
    console.log("🤖 AI generó configuración:", config);

    // Validate config
    if (!config || !config.formName || !config.fields) {
      console.error("❌ Configuración inválida:", config);
      this.messages.push({
        role: "assistant",
        content:
          "❌ Error: La configuración del formulario generada no es válida. Por favor, inténtalo de nuevo.",
      });
      return;
    }

    this.generatedConfig = config;
    this.showPreviewDialog = true;
  }

  private createFormFromGenerated() {
    console.log("🖱️ Click en Crear Formulario");
    console.log("💾 Guardando formulario:", this.generatedConfig);

    if (this.generatedConfig) {
      this.$emit("form-generated", this.generatedConfig);
      this.showPreviewDialog = false;
      this.resetChat();

      console.log("✅ Formulario enviado al componente padre");
    } else {
      console.error("❌ No hay configuración para guardar");
    }
  }

  private resetChat() {
    this.messages = [];
    this.userInput = "";
    this.loading = false;
    this.fetchingApi = false;
    this.aiService.reset();
  }

  private scrollToBottom() {
    this.$nextTick(() => {
      const container = this.$refs.chatContainer as HTMLElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  public get formName(): string {
    return this.generatedConfig ? this.generatedConfig.formName : "N/A";
  }

  public get tabCount(): number {
    return this.generatedConfig && this.generatedConfig.tabs
      ? this.generatedConfig.tabs.length
      : 0;
  }

  public get fieldCount(): number {
    return this.generatedConfig && this.generatedConfig.fields
      ? this.generatedConfig.fields.length
      : 0;
  }

  public get hasApiConfig(): string {
    return this.generatedConfig && this.generatedConfig.apiConfig ? "Sí" : "No";
  }

  private formatMessage(content: string): string {
    // Convert markdown-style formatting to HTML
    return content
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/`(.*?)`/g, "<code>$1</code>")
      .replace(/\n/g, "<br>");
  }
}
